import React, { useEffect, useRef, useState } from "react";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import BaseButton from "../../../components/ui/BaseButton";
import ProfilePictureAvatar from "../../../components/ui/ProfilePictureAvatar";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { markProfileEdited, submitEngineerProfile } from "../../../services/api/user";
import { useNavigate } from "react-router-dom";
import { submitCompanyProfile } from "../../../services/api/company";
import { submitCustomerProfile } from "../../../services/api/client";

const Header = ({ isAllowToSubmit, setIsAllowToSubmit }) => {
  const { userData, refetchMe } = useCurrentUser();

  // Required-section completion drives the Submit gate for individual engineers:
  // Verify Identity, Tax, Billing Rates and Resume must all be complete.
  const addr = userData?.personalAddress || {};
  const identityComplete = !!(
    userData?.nationality &&
    userData?.identityDocuments?.length &&
    addr.addressLine1 &&
    addr.country &&
    addr.city &&
    addr.state
  );
  const taxComplete = !!(userData?.australianBusinessNumber || userData?.isForeignContractor);
  const billingComplete = !!(
    userData?.billingRates?.length && userData?.billingRates?.every((rate) => Number(rate.rate) > 0)
  );
  const resumeComplete = !!userData?.resume;

  // Freelance company members only get the Verify Identity and Tax Information steps
  // (Insurance and Profile Details are optional for them), so the Submit gate is those
  // two — the same sections their left-nav marks as required.
  const isProfileReady = userData?.isCompanyMember
    ? identityComplete && taxComplete
    : identityComplete && taxComplete && billingComplete && resumeComplete;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isSubmitted = userData?.status === "SUBMITTED";

  // A submitted profile stays editable. Any save made on this page after submitting
  // means the admin is reviewing an out-of-date profile, so it has to be sent again.
  const [needsResubmit, setNeedsResubmit] = useState(false);
  // Bumped once per save; used as the button's key so its highlight replays each time.
  const [saveCount, setSaveCount] = useState(0);
  const countedSavesRef = useRef(new Set());
  useEffect(() => {
    if (!isSubmitted) return undefined;
    return queryClient.getMutationCache().subscribe((event) => {
      const mutation = event.mutation;
      if (
        event.type === "updated" &&
        mutation?.state.status === "success" &&
        mutation.options.mutationKey?.[0] !== "submitProfile" &&
        !countedSavesRef.current.has(mutation.mutationId)
      ) {
        countedSavesRef.current.add(mutation.mutationId);
        setNeedsResubmit(true);
        setSaveCount((count) => count + 1);
        markProfileEdited().then(() => refetchMe?.());
      }
    });
  }, [isSubmitted, queryClient, userData?.id]);

  useEffect(() => {
    if (isSubmitted && userData?.profileNeedsResubmit) setNeedsResubmit(true);
  }, [isSubmitted, userData?.profileNeedsResubmit]);

  const afterSubmit = () => setNeedsResubmit(false);

  const {
    isPending: isSubmitEngineerProfileApiLoading,
    error: isSubmitEngineerProfileApiError,
    mutateAsync: submitEngineerProfileMutation,
  } = useMutation({
    mutationKey: ["submitProfile"],
    mutationFn: submitEngineerProfile,
    enabled: false,
  });
  const { isPending: isSubmitCompanyProfileApiLoading, mutateAsync: submitCompanyProfileMutation } =
    useMutation({
      mutationKey: ["submitProfile"],
      mutationFn: submitCompanyProfile,
      enabled: false,
    });

  const handleProfileSubmit = async () => {
    try {
      await submitEngineerProfileMutation();
      setIsAllowToSubmit(false);
      afterSubmit();
      navigate("/jobs");
    } catch (error) {}
  };
  const {
    isPending: isSubmitCustomerProfileApiLoading,
    mutateAsync: submitCustomerProfileMutation,
  } = useMutation({
    mutationKey: ["submitProfile"],
    mutationFn: submitCustomerProfile,
  });

  const handleCustomerProfileSubmit = async () => {
    try {
      await submitCustomerProfileMutation();
      setIsAllowToSubmit(false);
      afterSubmit();
      await refetchMe?.();
      navigate("/workOrders");
    } catch (error) {
      console.error("Error submitting customer profile:", error);
    }
  };

  const handleCompanyProfileSubmit = async () => {
    try {
      await submitCompanyProfileMutation();
      setIsAllowToSubmit(false);
      afterSubmit();
      if (userData?.role?.role === "FREELANCER") {
        navigate("/jobs");
      } else {
        navigate("/workBoard/workOrders");
      }
    } catch (error) {
      console.error("Error submitting company profile:", error);
    }
  };

  return (
    <div className="bg-white">
      <div className="flex items-center justify-between py-2 xl:mx-40 mx-5">
        <h6>Complete your Tech Domain profile</h6>
        {/* <h6 className="font-normal">Having trouble? Visit our Help Center</h6> */}
      </div>

      <div className="border-slate-300	border-y">
        <div className="py-4 flex flex-wrap gap-4 justify-between items-center xl:mx-40 mx-5">
          <div className="flex items-center min-w-0">
            <ProfilePictureAvatar
              size={{ xs: 64, sm: 90 }}
              showEditBadge={false}
              profilePicture={userData?.picture}
              firstName={userData?.firstName}
              lastName={userData?.lastName}
              refetchMe={refetchMe}
            />
            <div className="ml-4 sm:ml-6 min-w-0">
              <h5 className="font-normal capitalize break-words">
                Welcome, {`${userData?.firstName || ""} ${userData?.lastName || ""}`}
              </h5>
              <h6>Here's what we need to complete your account setup.</h6>
              {needsResubmit && (
                <p className="text-amber-600 text-sm font-normal mt-1">
                  You&apos;ve updated your profile since your last submission. Please resubmit it for verification.
                </p>
              )}
            </div>
          </div>

          <div className="w-full sm:w-auto flex justify-end">
            {/* Submitted and unchanged: nothing to send. After an edit it reopens. */}
            {isSubmitted && !needsResubmit ? (
              <BaseButton disabled color="primary" className="py-[6px] px-[24px] rounded-full">
                Submitted
              </BaseButton>
            ) : userData?.role?.role === "CUSTOMER" ? (
              <BaseButton
                key={saveCount}
                disabled={!isAllowToSubmit}
                isLoading={isSubmitCustomerProfileApiLoading}
                loaderSize="24px"
                color="primary"
                className={`py-[6px] px-[24px] rounded-full ${needsResubmit ? "animate-signupCue" : ""}`}
                onClick={handleCustomerProfileSubmit}
              >
                {needsResubmit ? "Resubmit" : "Submit"}
              </BaseButton>
            ) : userData?.role?.role === "FREELANCER" && !userData?.isCompanyOwner ? (
              <BaseButton
                key={saveCount}
                disabled={!isProfileReady}
                isLoading={isSubmitEngineerProfileApiLoading}
                loaderSize="24px"
                color="primary"
                className={`py-[6px] px-[24px] rounded-full ${needsResubmit ? "animate-signupCue" : ""}`}
                onClick={handleProfileSubmit}
              >
                {needsResubmit ? "Resubmit" : "Submit"}
              </BaseButton>
            ) : (
              <BaseButton
                key={saveCount}
                disabled={!isAllowToSubmit}
                isLoading={isSubmitCompanyProfileApiLoading}
                loaderSize="24px"
                color="primary"
                className={`py-[6px] px-[24px] rounded-full ${needsResubmit ? "animate-signupCue" : ""}`}
                onClick={handleCompanyProfileSubmit}
              >
                {needsResubmit ? "Resubmit" : "Submit"}
              </BaseButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
